/**
 * Page view shortcuts. Browser fields auto-detected when available.
 */
import { PageViews } from "../../features/analytics/page-views";

function browserDefaults() {
  const isBrowser = typeof window !== "undefined" && typeof document !== "undefined";
  return {
    url: isBrowser ? window.location.href : "",
    path: isBrowser ? window.location.pathname : "",
    title: isBrowser ? document.title : undefined,
    referrer: isBrowser ? document.referrer : undefined,
  };
}

export class PageViewTracker {
  constructor(public readonly client: PageViews) {}

  /**
   * Track a page view. With no args, auto-detects from `window`/`document`.
   * Pass a path or full URL to override.
   */
  view(
    pathOrUrl?: string,
    properties?: Record<string, unknown>,
    opts?: { title?: string; referrer?: string; userId?: string; sessionId?: string }
  ) {
    const d = browserDefaults();
    const isFullUrl = pathOrUrl && /^https?:\/\//i.test(pathOrUrl);
    const url = isFullUrl ? pathOrUrl! : d.url;
    const path = isFullUrl ? new URL(pathOrUrl!).pathname : (pathOrUrl ?? d.path);
    return this.client.track({
      url,
      path,
      title: opts?.title ?? d.title,
      referrer: opts?.referrer ?? d.referrer,
      userId: opts?.userId,
      sessionId: opts?.sessionId,
      properties,
    });
  }

  /** Record scroll depth (0-100). */
  scroll(path: string, scrollDepth: number, timeAtDepth?: number) {
    return this.client.trackScrollDepth({ path, scrollDepth, timeAtDepth });
  }

  /** Record time spent on a page (seconds). */
  time(path: string, duration: number) {
    return this.client.trackTimeOnPage({ path, duration });
  }

  /** Record page exit. */
  exit(path: string, duration: number, scrollDepth: number) {
    return this.client.trackExit(path, duration, scrollDepth);
  }

  /** Auto-track SPA route changes, scroll-depth thresholds, and time-on-page. */
  autoTrack(options?: { userId?: string; sessionId?: string }) {
    return this.client.autoTrack(options);
  }
}
